import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState(['', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const pinRefs = [useRef(), useRef(), useRef(), useRef(), useRef()];

  const rawPin = pin.join('');
  const isReady = username.trim().length >= 3 && rawPin.length === 5;

  const doLogin = async (pinVal) => {
    if (loading) return;
    if (!username.trim() || username.trim().length < 3) return;
    if (pinVal.length !== 5) return;
    setLoading(true);
    try {
      await login(username.trim(), pinVal);
      toast('Bienvenue sur Elite Services Admin 👋', 'success');
      navigate('/', { replace: true });
    } catch (err) {
      toast(err.response?.data?.message || 'Identifiants incorrects', 'error');
      setPin(['', '', '', '', '']);
      pinRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...pin];
    next[index] = digit;
    setPin(next);
    if (digit && index < 4) pinRefs[index + 1].current?.focus();
    // Auto-soumission dès que le 5e chiffre est saisi
    if (digit && index === 4) {
      const fullPin = next.join('');
      if (fullPin.length === 5 && username.trim().length >= 3) {
        pinRefs[4].current?.blur();
        doLogin(fullPin);
      }
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      pinRefs[index - 1].current?.focus();
    }
  };

  const handlePinPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
    if (pasted.length === 5) {
      setPin(pasted.split(''));
      pinRefs[4].current?.blur();
      // Auto-soumission après collage du PIN complet
      if (username.trim().length >= 3) {
        setTimeout(() => doLogin(pasted), 50);
      }
    }
    e.preventDefault();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isReady) return;
    doLogin(rawPin);
  };

  return (
    <div style={S.page}>
      {/* Left — branding */}
      <div style={S.left}>
        <div style={S.leftContent}>
          <img src="/logo.png" alt="Elite Services" style={S.logo} />
          <h1 style={S.brand}>Elite Services</h1>
          <p style={S.tagline}>La plateforme de services domestiques de confiance</p>
          <div style={S.slogan}>
            <span style={{ color: '#3b82f6' }}>✦ Connectivité</span>
            <span style={S.sloganSep}>·</span>
            <span style={{ color: '#10b981' }}>✦ Fiabilité</span>
            <span style={S.sloganSep}>·</span>
            <span style={{ color: '#f5a623' }}>✦ Rapidité</span>
          </div>
          <div style={S.features}>
            {[
              '👥 Gestion des prestataires',
              '💳 Suivi des paiements',
              '📊 Statistiques en temps réel',
              '🔒 Sécurité renforcée',
            ].map(f => <div key={f} style={S.feature}>{f}</div>)}
          </div>
        </div>
        <div style={S.leftBg} />
      </div>

      {/* Right — form */}
      <div style={S.right}>
        <div style={S.formCard} className="animate-scale">
          <div style={{ marginBottom: 32 }}>
            <h2 style={S.formTitle}>Connexion Admin</h2>
            <p style={S.formSub}>Accès réservé aux administrateurs</p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Identifiant */}
            <div className="form-group">
              <label className="form-label">Identifiant</label>
              <div style={{ position: 'relative' }}>
                <span style={S.inputIcon}>👤</span>
                <input
                  className="form-input"
                  style={{ paddingLeft: 40 }}
                  placeholder="ex: admin"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  spellCheck={false}
                  autoCapitalize="none"
                />
              </div>
            </div>

            {/* PIN 5 cases */}
            <div className="form-group">
              <label className="form-label">Code PIN (5 chiffres)</label>
              <div style={S.pinRow} onPaste={handlePinPaste}>
                {pin.map((digit, i) => (
                  <input
                    key={i}
                    ref={pinRefs[i]}
                    type="password"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handlePinChange(i, e.target.value)}
                    onKeyDown={e => handlePinKeyDown(i, e)}
                    style={{
                      ...S.pinBox,
                      borderColor: digit ? 'var(--primary)' : 'var(--border)',
                      background: digit ? 'var(--primary-light)' : 'var(--surface)',
                      boxShadow: digit ? '0 0 0 3px rgba(233,69,96,0.12)' : 'none',
                    }}
                    autoComplete="one-time-code"
                  />
                ))}
              </div>
            </div>

            <button
              className="btn btn-primary btn-lg"
              type="submit"
              disabled={loading || !isReady}
              style={{ width: '100%', marginTop: 8, justifyContent: 'center' }}
            >
              {loading
                ? <><div className="spinner" style={{ width: 16, height: 16, borderTopColor: '#fff' }} /> Connexion...</>
                : 'Se connecter →'}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: 'var(--text-muted)' }}>
            © {new Date().getFullYear()} Elite Services · Tous droits réservés
          </p>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: { display: 'flex', minHeight: '100vh' },
  left: {
    flex: 1, background: 'var(--secondary)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 48, position: 'relative', overflow: 'hidden',
  },
  leftBg: {
    position: 'absolute', inset: 0,
    background: 'radial-gradient(ellipse at 30% 50%, rgba(233,69,96,0.15) 0%, transparent 60%)',
    pointerEvents: 'none',
  },
  leftContent: { position: 'relative', zIndex: 1, maxWidth: 400 },
  logo: {
    width: 100, height: 100, objectFit: 'contain',
    marginBottom: 20, borderRadius: 20,
    filter: 'drop-shadow(0 8px 24px rgba(233,69,96,0.35))',
  },
  brand: { fontSize: 32, fontWeight: 800, color: '#fff', marginBottom: 12 },
  tagline: { fontSize: 16, color: 'rgba(255,255,255,0.6)', marginBottom: 20, lineHeight: 1.6 },
  slogan: {
    display: 'flex', alignItems: 'center', gap: 8,
    marginBottom: 32, fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
  },
  sloganSep: { color: 'rgba(255,255,255,0.25)', fontSize: 16 },
  features: { display: 'flex', flexDirection: 'column', gap: 12 },
  feature: { fontSize: 14, color: 'rgba(255,255,255,0.75)' },
  right: {
    width: 480, display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: 40, background: 'var(--bg)',
  },
  formCard: { width: '100%', maxWidth: 380 },
  formTitle: { fontSize: 26, fontWeight: 800, color: 'var(--text-primary)' },
  formSub: { fontSize: 14, color: 'var(--text-secondary)', marginTop: 6 },
  inputIcon: {
    position: 'absolute', left: 12, top: '50%',
    transform: 'translateY(-50%)', fontSize: 15, pointerEvents: 'none',
  },
  pinRow: { display: 'flex', gap: 10 },
  pinBox: {
    width: 52, height: 56,
    textAlign: 'center', fontSize: 22, fontWeight: 700,
    border: '2px solid', borderRadius: 10,
    outline: 'none', transition: 'all 0.15s ease',
    fontFamily: 'inherit', cursor: 'text',
  },
};
